import useApiRequest from "@/lib/hooks/use-request";
import {
  CreateDealRequestDto,
  DealDetailsResponseDto,
  DealsCreationEntitiesResponseDto,
  DealsPipelineSelectableEntityResponseDto,
} from "@/lib/interfaces/deals";
import { Paginated, Pagination } from "@/lib/interfaces/utils";
import { PersonListResponseDto } from "@/lib/interfaces/persons";
import { Button } from "@/ui-components/ui/button";
import DataFetchParent from "@/ui-components/ui/data-fetch";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui-components/ui/form";
import { Input } from "@/ui-components/ui/input";
import LinkedItemManager from "@/ui-components/ui/linked-item-manager";
import { Textarea } from "@/ui-components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/ui-components/ui/select";
import { zodResolver } from "@hookform/resolvers/zod";
import { Crosshair, User, Building, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { ControllerRenderProps, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { z } from "zod";
import PipelineEditor from "../pipelines/pipeline-editor";
import { toast } from "sonner";

interface Preview {
  title?: string;
  id?: string;
}

interface ContactOption {
  _id: string;
  fullName: string;
  email?: string;
  organization?: string;
}

const DealsEditorEnhanced = ({ pipelineId }: { pipelineId?: string }) => {
  const navigate = useNavigate();
  const { apiRequest } = useApiRequest();
  const [dealCreationEntities, setDealCreationEntities] = useState<DealsCreationEntitiesResponseDto>();
  const [isLoading, setIsLoading] = useState(true);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contacts, setContacts] = useState<ContactOption[]>([]);
  const [organizations, setOrganizations] = useState<any[]>([]);

  // Function to fetch ALL contacts with proper pagination
  const fetchContacts = async () => {
    setContactsLoading(true);
    let allContacts: ContactOption[] = [];
    let currentPage = 1;
    let hasMore = true;
    
    try {
      while (hasMore && currentPage <= 5) { // Limit to 5 pages = 100 contacts max
        const url = `contacts/persons?limit=20&page=${currentPage}`;
        const response = await apiRequest<Paginated<PersonListResponseDto>>(url, "GET");
        
        if (response.data && response.data.docs) {
          const pageContacts = response.data.docs.map((person) => ({
            _id: person._id,
            fullName: `${person.firstName || ''} ${person.lastName || ''}`.trim() || 'Unnamed',
            email: person.mail || '',
            organization: person.organizationName || ''
          }));
          
          allContacts = [...allContacts, ...pageContacts];
          
          // Check if there are more pages
          hasMore = response.data.hasNextPage || false;
          currentPage++;
        } else {
          hasMore = false;
        }
      }
      
      setContacts(allContacts);
      console.log(`Loaded ${allContacts.length} contacts total`);
      
      if (allContacts.length === 0) {
        toast.info('Keine Kontakte gefunden. Erstellen Sie zuerst Kontakte unter "Kontakte → Personen".');
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Fehler beim Laden der Kontakte');
      setContacts([]);
    } finally {
      setContactsLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      // Fetch deal creation entities
      const res = await apiRequest<DealsCreationEntitiesResponseDto>("deals/creation-entities", "GET");
      setIsLoading(false);
      if (res.data) {
        setDealCreationEntities(res.data);
        const selectedPipeline = res.data.pipelines.find((pipeline) => pipeline._id === pipelineId);
        form.setValue("pipeline", { 
          id: selectedPipeline?._id || "",
          title: selectedPipeline?.value || ""
        });
      }

      // Fetch contacts using the dedicated function
      await fetchContacts();

      // Fetch organizations with proper pagination
      try {
        let allOrganizations: any[] = [];
        let currentPage = 1;
        let hasMore = true;
        
        while (hasMore && currentPage <= 5) { // Limit to 5 pages
          const orgsRes = await apiRequest<any>(`contacts/organizations?limit=20&page=${currentPage}`, "GET");
          
          if (orgsRes.data && orgsRes.data.docs) {
            allOrganizations = [...allOrganizations, ...orgsRes.data.docs];
            hasMore = orgsRes.data.hasNextPage || false;
            currentPage++;
          } else {
            hasMore = false;
          }
        }
        
        setOrganizations(allOrganizations);
        console.log(`Loaded ${allOrganizations.length} organizations`);
        console.log('Organizations:', allOrganizations);
      } catch (error) {
        console.error('Error fetching organizations:', error);
        setOrganizations([]);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRequest, pipelineId]);

  const dealSchema = z.object({
    title: z
      .string()
      .min(1, { message: "Titel ist erforderlich" })
      .max(100, { message: "Titel darf nicht mehr als 100 Zeichen lang sein" }),
    pipeline: z.object({
      title: z.string().optional(),
      id: z.string().min(1, { message: "Pipeline wird benötigt" }),
    }),
    belongsToPersonId: z.string().optional(),
    belongsToOrganizationId: z.string().optional(),
    information: z.string().optional(),
  });

  type DealFormSchema = z.infer<typeof dealSchema>;

  const form = useForm<DealFormSchema>({
    resolver: zodResolver(dealSchema),
    defaultValues: {
      title: "",
      pipeline: {
        id: pipelineId || "",
        title: "",
      },
      belongsToPersonId: "",
      belongsToOrganizationId: "",
      information: "",
    },
  });

  const onSubmit = async (data: DealFormSchema) => {
    // Step 1: Create the deal with basic info
    const createReq = {
      title: data.title,
      belongsToPipelineId: data.pipeline.id || "",
      information: data.information,
    };

    const res = await apiRequest<DealDetailsResponseDto>("deals", "POST", {
      body: createReq,
    });

    if (res.data) {
      // Step 2: Update the deal with contact and organization if provided
      if (data.belongsToPersonId || data.belongsToOrganizationId) {
        const updateReq: any = {
          title: data.title, // Required field for update
          information: data.information,
        };
        
        if (data.belongsToPersonId) {
          updateReq.belongsToPersonId = data.belongsToPersonId;
        }
        if (data.belongsToOrganizationId) {
          updateReq.belongsToOrganizationId = data.belongsToOrganizationId;
        }

        const updateRes = await apiRequest<DealDetailsResponseDto>(`deals/${res.data._id}`, "PATCH", {
          body: updateReq,
        });

        if (updateRes.data) {
          toast.success("Deal mit Kontakt erfolgreich erstellt");
          navigate(`/deals/${res.data._id}`);
        } else {
          toast.warning("Deal erstellt, aber Kontakt konnte nicht verknüpft werden");
          navigate(`/deals/${res.data._id}`);
        }
      } else {
        toast.success("Deal erfolgreich erstellt");
        navigate(`/deals/${res.data._id}`);
      }
    }
  };

  const onRemove = async (field: ControllerRenderProps<any, any>) => {
    field.onChange(undefined);
  };

  const onAssign = async (
    itemPreview: Preview,
    field: ControllerRenderProps<any, any>
  ) => {
      field.onChange(itemPreview);
  };

  // When contact changes, auto-populate organization if available
  const handleContactChange = (contactId: string) => {
    const contact = contacts.find(c => c._id === contactId);
    if (contact?.organization) {
      const org = organizations.find(o => o.name === contact.organization);
      if (org) {
        form.setValue('belongsToOrganizationId', org._id);
      }
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-5 h-full">
        <div className="space-y-6">
          <div className="space-y-4">
            <div className="flex flex-col gap-2">
              <FormLabel className="font-medium">Titel:*</FormLabel>
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input {...field} placeholder="Deal Titel" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Contact Person Selection */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <FormLabel className="font-medium flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Kontaktperson:
                </FormLabel>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={fetchContacts}
                  disabled={contactsLoading}
                  className="h-8 px-2"
                >
                  <RefreshCw className={`h-4 w-4 ${contactsLoading ? 'animate-spin' : ''}`} />
                  <span className="ml-1">Aktualisieren</span>
                </Button>
              </div>
              <FormField
                control={form.control}
                name="belongsToPersonId"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Select 
                        value={field.value} 
                        onValueChange={(value) => {
                          field.onChange(value);
                          handleContactChange(value);
                        }}
                        disabled={contactsLoading}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={contactsLoading ? "Lade Kontakte..." : `Kontaktperson auswählen (${contacts.length} verfügbar)`} />
                        </SelectTrigger>
                        <SelectContent>
                          {contacts.length === 0 ? (
                            <SelectItem value="_" disabled>Keine Kontakte gefunden</SelectItem>
                          ) : (
                            contacts.map((contact) => (
                              <SelectItem key={contact._id} value={contact._id}>
                                <div className="flex flex-col">
                                  <span>{contact.fullName}</span>
                                  {contact.email && (
                                    <span className="text-xs text-gray-500">{contact.email}</span>
                                  )}
                                  {contact.organization && (
                                    <span className="text-xs text-gray-400">{contact.organization}</span>
                                  )}
                                </div>
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                    {contacts.length > 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        {contacts.length} Kontakte verfügbar. Erstellen Sie neue Kontakte unter "Kontakte → Personen".
                      </p>
                    )}
                  </FormItem>
                )}
              />
            </div>

            {/* Organization Selection */}
            <div className="flex flex-col gap-2">
              <FormLabel className="font-medium flex items-center gap-2">
                <Building className="h-4 w-4" />
                Organisation:
              </FormLabel>
              <FormField
                control={form.control}
                name="belongsToOrganizationId"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger>
                          <SelectValue placeholder="Organisation auswählen (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          {organizations.length === 0 ? (
                            <SelectItem value="_" disabled>Keine Organisationen gefunden</SelectItem>
                          ) : (
                            organizations.map((org) => (
                              <SelectItem key={org._id} value={org._id}>
                                {org.name}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex flex-col gap-2">
              <FormLabel className="font-medium">Pipeline:*</FormLabel>
              <FormField
                control={form.control}
                name="pipeline"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <DataFetchParent
                        data={dealCreationEntities}
                        isLoading={isLoading}
                        renderElement={(pipelines: DealsCreationEntitiesResponseDto) => (
                          <PipelineAssigner
                            pipelines={pipelines.pipelines}
                            item={field.value}
                            onAssign={async (pipeline) => {
                              onAssign(pipeline, field);
                            }}
                            onRemove={async () => {
                              onRemove(field);
                            }}
                          />
                        )}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex flex-col gap-2">
              <FormLabel className="font-medium">Notizen:</FormLabel>
              <FormField
                control={form.control}
                name="information"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Textarea 
                        {...field} 
                        placeholder="Zusätzliche Informationen zum Deal" 
                        rows={4}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>
        <Button type="submit" variant="default" className="text-white font-bold flex items-center gap-4 w-fit self-end">
          <Crosshair className="h-4 w-4" />
          <span>Deal erstellen</span>
        </Button>
      </form>
    </Form>
  );
};

const PipelineAssigner = ({
  item,
  onAssign,
  onRemove,
  pipelines,
}: {
  item?: Preview;
  onAssign: (pipeline: Preview) => Promise<void>;
  onRemove: () => Promise<void>;
  pipelines: DealsPipelineSelectableEntityResponseDto[];
}) => {
  const fetchSearchResults = useCallback(
    async (
      pagination: Pagination,
      searchText: string
    ): Promise<Paginated<DealsPipelineSelectableEntityResponseDto>> => {
      try {
        const searchedPipelines = pipelines.filter((pipeline) =>
          pipeline.value.toLowerCase().includes(searchText.toLowerCase())
        );

        return {
          docs: searchedPipelines,
          hasNextPage: false,
          limit: pagination.pageSize,
          offset: pagination.pageIndex * pagination.pageSize,
          page: pagination.pageIndex,
          hasPrevPage: pagination.pageIndex > 0,
          count: searchedPipelines.length,
          pageCount: 1,
        };
      } catch (error) {
        console.error("Error fetching pipelines:", error);
        return {
          docs: [],
          hasNextPage: false,
          limit: pagination.pageSize,
          offset: pagination.pageIndex * pagination.pageSize,
          page: pagination.pageIndex,
          hasPrevPage: pagination.pageIndex > 0,
          count: 0,
          pageCount: 0,
        };
      }
    },
    [pipelines]
  );

  const renderSearchOption = useCallback(
    (pipeline: DealsPipelineSelectableEntityResponseDto) => ({
      label: pipeline.value,
      value: pipeline._id,
    }),
    []
  );

  return (
    <LinkedItemManager<DealsPipelineSelectableEntityResponseDto>
      editor={<PipelineEditor withoutRedirect={true} />}
      fetchSearchResults={fetchSearchResults}
      renderSearchOption={renderSearchOption}
      customOnSubmit={async (id, title) => {
        await onAssign({ id, title });
      }}
      removeItem={async () => onRemove()}
      link={
        item && item.id && item.title
          ? {
              title: item.title,
              href: `/pipelines/${item.id}`,
              id: item.id,
            }
          : undefined
      }
      title="Pipeline"
    />
  );
};

export default DealsEditorEnhanced;
